import React from 'react';
import dayjs from 'dayjs';
import { SellerProductBasicDetails } from './types';
import { useTranslation } from 'react-i18next';
import TitleText from '../../atoms/TitleText';
import InfoText from '../../atoms/InfoText';

type Props = {
  basicDetails?: SellerProductBasicDetails;
};

export default function PricingAvailabilityCard({ basicDetails }: Props) {
  const { t } = useTranslation();

  const {
    priceUnit,
    priceUnitConverted,
    // productPrice,
    // productPriceConverted,
    minQty,
    pricingTiers,
    awardsYear,
    listedOn,
    stock,
    currency,
  } = basicDetails || {};


  return (
    <div
      className="bg-white dark:bg-gray-900 rounded-[12px] p-6 border border-light-input-border dark:border-dark-input-border flex flex-col gap-6"
      data-testid="seller-product-pricing-card"
    >
      {/* Top Details (Stock, Type, Dates) */}
      <div className="flex flex-col gap-0 border-b border-gray-100 dark:border-gray-800 pb-2">
        <div
          className={` ${stock == undefined ? 'hidden' : ''} flex  justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800 last:border-0`}
        >
          <InfoText
            text={t('components.productDetailsPage.stockUnit')}
            className="!text-gray-400 !text-sm !m-0"
          />
          <InfoText
            text={stock !== undefined ? `${stock} unit` : '—'}
            className="!font-bold !text-[#090909] dark:!text-white !text-sm !m-0"
          />
        </div>

        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
          <InfoText
            text={t('components.productDetailsPage.productType')}
            className="!text-gray-400 !text-sm !m-0"
          />
          <InfoText
            text={basicDetails?.priceType === 'CART' ? 'Cart' : t('components.productDetailsPage.rfq')}
            className="!font-bold !text-[#090909] dark:!text-white !text-sm !m-0"
          />
        </div>

        {awardsYear && (
          <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
            <InfoText
              text={t('components.productDetailsPage.awardYear', {
                year: awardsYear,
              })}
              className="!text-gray-400 !text-sm !m-0"
            />
            <InfoText
              text={t('components.productDetailsPage.uponRequest')}
              className="!font-bold !text-[#090909] dark:!text-white !text-sm !m-0"
            />
          </div>
        )}

        {listedOn && (
          <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800 last:border-0">
            <InfoText
              text={t('components.productDetailsPage.listedOn')}
              className="!text-gray-400 !text-sm !m-0"
            />
            <InfoText
              text={dayjs(listedOn).format('DD MMMM YYYY')}
              className="!font-bold !text-[#090909] dark:!text-white !text-sm !m-0"
            />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6">
        <TitleText
          text={t('components.productDetailsPage.pricingAvailability')}
          size="base"
          className="!font-bold !text-[#090909] dark:!text-white"
        />

        <div className="bg-[#FFF8F1] dark:bg-orange-900/10 p-4 rounded-lg flex items-center justify-between">
          <div className="flex flex-col items-start gap-1">
            <InfoText
              text={t('components.productDetailsPage.pricePerUnit')}
              size="sm"
              className="!m-0 !text-[#F58F29]"
            />
            <TitleText
              text={priceUnitConverted ? `${priceUnitConverted} ${currency?.code || 'SAR'}` : '—'}
              size="2xl"
              className="!font-bold !text-[#F58F29]"
            />
          </div>

          <div className="flex flex-col items-center gap-1">
            <InfoText
              text={t('components.productDetailsPage.minOrder')}
              size="sm"
              className="!m-0 !text-[#F58F29] text-center"
            />
            <TitleText
              text={minQty ? String(minQty) : '—'}
              size="2xl"
              className="!font-bold !text-[#F58F29] text-center"
            />
          </div>
        </div>

        {pricingTiers && pricingTiers.length > 0 && (
          <div className="border border-light-input-border dark:border-dark-input-border rounded-xl p-5 flex flex-col gap-4">
            <TitleText
              text={t('components.productDetailsPage.bulkTierPricing')}
              className="!font-bold !text-gray-900 dark:!text-white !text-lg"
            />

            <div className="flex flex-col gap-3">
              {pricingTiers.map((tier, idx) => {
                const isLowestTier = idx === pricingTiers.length - 1;
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 border-b last:border-0 border-gray-100 dark:border-gray-800"
                  >
                    <div className="flex flex-col gap-1">
                      <InfoText
                        text={
                          tier.internalLabel ||
                          t('components.productDetailsPage.tierNum', {
                            num: idx + 1,
                          })
                        }
                        size="sm"
                        className="!text-[#090909] dark:!text-gray-300 !font-normal !m-0"
                      />
                      <TitleText
                        text={`${tier.minQty} - ${tier.maxQty || '+'} units`}
                        size="sm"
                        className="!font-bold !text-[#090909] dark:!text-white !m-0"
                      />
                    </div>
                    <div className="flex items-baseline gap-1">
                      <TitleText
                        text={String(tier.pricePerUnit.toFixed(2))}
                        size="base"
                        className={`!font-bold !m-0 ${isLowestTier ? '!text-light-success' : '!text-[#090909] dark:!text-white'}`}
                      />
                      <InfoText
                        text={currency?.code || 'SAR'}
                        size="sm"
                        className={`!font-normal !m-0 ${isLowestTier ? '!text-light-success' : '!text-[#090909] dark:!text-white'}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
